"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { Item, ItemType, Kit, Asset } from "@thenounproject/lingo-core";
import { useSelection, useSelectionActions } from "@contexts/SelectedItemsProvider";
import { useGetAssets, useGetItems } from "@selectors/getters";
import { useClearInspectorOnClick, useShiftKey } from "@helpers/selection";
import { handleInspectorItemClick } from "@helpers/items";
import { Inspectable } from "@constants/Inspector";

export type KitSelectionOptions = {
  kit: Kit;
  versionNum: number;
  /** Whether viewing trashed items (affects item filtering) */
  showTrashedItems?: boolean;
  /** Key that changes when page changes (triggers selection clear) */
  displayKey?: string;
};

export type KitSelectionReturn = {
  selectedItems: string[];
  setSelectedItems: (ids: string[]) => void;
  clearSelection: () => void;
  onSelectItem: (event: React.MouseEvent, itemId: string, allItems: Item[]) => void;
  inspectables: Inspectable[];
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | undefined>;
  disableTextSelection: boolean;
  canEditContent: boolean;
};

/**
 * Hook that encapsulates all kit item selection logic.
 * Used by Kit, KitTrashedItems, KitRecoveredAssets pages.
 */
export default function useKitSelection({
  kit,
  versionNum,
  showTrashedItems = false,
  displayKey = "",
}: KitSelectionOptions): KitSelectionReturn {
  // Kit content CRUD permission
  const canEditContent = useMemo(
    () => kit?.access?.permissions?.includes("edit_content") && versionNum === 0,
    [kit, versionNum]
  );

  const assets = useGetAssets();
  const allItems = useGetItems();

  // Selection state
  const { setSelectedItems: _setSelectedItems, clearSelection: _clearSelection } =
    useSelectionActions();
  const selectedItems = useSelection("kitGallery");

  // Store the latest selectedItems in a ref to avoid recreating onSelectItem callback
  const selectedItemsRef = useRef(selectedItems);
  selectedItemsRef.current = selectedItems;

  const cachedSelectedItems = useRef<string[] | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>();

  const setSelectedItems = useCallback(
    (ids: string[]) => {
      _setSelectedItems({ type: "kitGallery", ids });
      cachedSelectedItems.current = ids;
    },
    [_setSelectedItems]
  );

  const clearSelection = useCallback(() => {
    _clearSelection({ type: "kitGallery" });
    cachedSelectedItems.current = null;
  }, [_clearSelection]);

  // Filter selected items based on status
  const selectedKitItems = useMemo(
    () =>
      selectedItems
        .map(id => allItems[id])
        .filter(i => i?.status === (showTrashedItems ? "trashed" : "active")),
    [allItems, selectedItems, showTrashedItems]
  );

  // Build inspectables from selected items
  const inspectables: Inspectable[] = useMemo(
    () =>
      selectedKitItems.map(i => {
        const asset = assets[i.assetId] as Asset;
        return {
          item: { ...i, asset },
          asset,
        };
      }),
    [assets, selectedKitItems]
  );

  // Shift key handling for multi-select
  const shiftDown = useShiftKey();
  const disableTextSelection = shiftDown && selectedItems.length > 0;

  useEffect(() => {
    if (!shiftDown) cachedSelectedItems.current = null;
  }, [shiftDown]);

  // Handle item click with shift-select support
  const onSelectItem = useCallback(
    (event: React.MouseEvent, itemId: string, items: Item[]) => {
      let selectedBeforeClick = selectedItemsRef.current;
      let filteredItems: Item[] = items;
      if (event?.shiftKey) {
        if (!cachedSelectedItems.current || cachedSelectedItems.current.length === 0) {
          cachedSelectedItems.current = selectedItemsRef.current;
        }
        selectedBeforeClick = cachedSelectedItems.current;
        filteredItems = canEditContent ? items : items.filter(i => i.type === ItemType.asset);
      }
      handleInspectorItemClick(event, itemId, filteredItems, selectedBeforeClick, setSelectedItems);
    },
    [setSelectedItems, canEditContent]
  );

  // Clear selection when clicking outside inspector
  useClearInspectorOnClick(clearSelection);

  // Clear selection on page change
  useEffect(() => {
    clearSelection();
  }, [kit.kitId, versionNum, displayKey, clearSelection]);

  return {
    selectedItems,
    setSelectedItems,
    clearSelection,
    onSelectItem,
    inspectables,
    scrollContainerRef,
    disableTextSelection,
    canEditContent,
  };
}
